import { useEffect, useState } from 'react'
import { useAppDispatch } from '../../reducers/store'
import { IconX } from '@tabler/icons-react'
import DataGrid from '../data-grid/DataGrid'
import { TABLE_HEIGHT_WITH_TRIAL } from '../../strings/consts'
import { managePolicyColumns } from './ManagePolicyColumns'
import { getAllPoliciesForOrg, selectGroup, updatePoliciesForGroup } from '../../reducers/groups'
import { useSelector } from 'react-redux'
import { UpdatePoliciesForOrgUserGroupRequest } from '../../types/GroupTypes'
import { DEFAULT_TOAST_CONFIG } from '../toast/consts'
import toast from 'react-hot-toast'

interface IIntegrationPopoutProps {
  setShowPopout: (showPopout: boolean) => void
  groupId: string
  reloadGroupPolicies: () => void
}

function ManagePoliciesPopout(props: IIntegrationPopoutProps) {
  const { setShowPopout, groupId, reloadGroupPolicies } = props
  const di = useAppDispatch()
  const [isDirty, setIsDirty] = useState(false)
  const { allPoliciesForOrg } = useSelector(selectGroup)
  const [checkedItems, setCheckedItems] = useState<Record<number, boolean>>({})

  useEffect(() => {
    di(getAllPoliciesForOrg(Number(groupId)))
  }, [])

  const handleCheckboxChange = (id: number) => {
    if (checkedItems[id]) {
      setCheckedItems({
        ...checkedItems,
        [id]: false,
      })
    } else {
      setCheckedItems({
        ...checkedItems,
        [id]: true,
      })
    }
    setIsDirty(true)
  }

  useEffect(() => {
    if (!allPoliciesForOrg) return
    const initialState: Record<number, boolean> = {}
    for (const p of allPoliciesForOrg) {
      initialState[p.id] = p.assigned
    }
    setCheckedItems(initialState)
  }, [allPoliciesForOrg])

  const navigateAway = () => {
    if (isDirty) {
      if (window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
        setShowPopout(false)
      }
    } else {
      setShowPopout(false)
    }
  }

  const saveChanges = async () => {
    if (!allPoliciesForOrg) return

    const payload: UpdatePoliciesForOrgUserGroupRequest = {
      groupId: Number(groupId),
      updatedPolicies: allPoliciesForOrg.map((policy) => ({
        policyId: policy.id,
        enabled: checkedItems[policy.id] === true,
      })),
    }

    const updateSuccess = await di(updatePoliciesForGroup(payload))
    if (updateSuccess) {
      toast.success('Policies updated successfully', DEFAULT_TOAST_CONFIG)
      setIsDirty(false)
      reloadGroupPolicies()
      setShowPopout(false)
    } else {
      toast.error('Failed to update policies', DEFAULT_TOAST_CONFIG)
    }
  }

  return (
    <>
      <div className="bg-black bg-opacity-50 fixed top-0 left-0 w-full h-full z-50" onClick={() => navigateAway()}>
        <div
          className={`top-0 my-[7px] mr-[11px] rounded-lg h-[calc(100vh_-_14px)] fixed flex flex-col w-2/3 gap-6 right-0 bg-neutral-2 dark:bg-mainBackground z-50 border-mainBorder border-t border-l shadow-2xl p-4`}
          onClick={(e) => {
            e.stopPropagation()
          }}
        >
          <div className="w-full flex justify-between">
            <p className="text-mainText text-lg font-medium mt-1">Manage Policies</p>
            <div
              className="h-8 w-8 cursor-pointer border bg-white dark:bg-transparent rounded flex items-center justify-center"
              onClick={() => {
                if (isDirty) {
                  if (window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
                    setShowPopout(false)
                  }
                } else {
                  setShowPopout(false)
                }
              }}
            >
              <IconX size={22} />
            </div>
          </div>

          <DataGrid
            columns={managePolicyColumns(checkedItems, setCheckedItems, handleCheckboxChange)}
            data={allPoliciesForOrg}
            loading={allPoliciesForOrg === undefined}
            classNames={`calc(100% - ${TABLE_HEIGHT_WITH_TRIAL})`}
          />

          <div className="flex flex-col grow items-end justify-end">
            <hr className="w-full mb-4 border-1 border-neutral-4" />
            <div className="flex gap-2">
              <button
                className={`h-8 border border-mainBorder text-mainText bg-white px-3 rounded flex items-center justify-center cursor-pointer`}
                onClick={() => navigateAway()}
              >
                Cancel
              </button>
              <button
                className={`h-8 border border-transparent text-white bg-mainPurple px-3 rounded flex items-center justify-center cursor-pointer hover:bg-tertiaryPurple active:bg-tertiaryPurple`}
                onClick={() => saveChanges()}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default ManagePoliciesPopout
